/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/
import { IModelConnection, ScreenViewport, Viewport } from "@itwin/core-frontend";
import { ViewState, StandardViewId, ViewPose } from "@itwin/core-frontend";
import { ViewCreator2d, ViewCreator2dOptions, ViewCreator3d, ViewCreator3dOptions } from "@itwin/core-frontend";
import { ContentGroup, ContentGroupProps, ContentProps, FrontstageManager, IModelViewportControl } from "@itwin/appui-react";
import { StandardContentLayouts, ContentLayoutProps } from "@itwin/appui-abstract";
import { connectViewportFrusta, connectViewports, connectViewportViews, SynchronizeViewports } from "@itwin/core-frontend/lib/cjs/ViewportSync";
import { ColorDef } from "@itwin/core-common";

import { cView } from "./View";
import { cMarker } from "./Marker";
import { ClipVector } from "@itwin/core-geometry";

export interface cViewportConfig {
  id?: string;
  viewState?: ViewState
}

export class cMultiViewport {
  private static imodel: IModelConnection;
  private static vp: ScreenViewport;

  private static viewState2d: ViewState;
  private static viewStateCADTop: ViewState;
  private static viewStateCADBottom: ViewState;
  
  private static pose: ViewPose;
  private static clip: ClipVector;

  private static disconnect: any;

  //!!OA initialization call should use await - but disabled because viewState
  // creation taking too much time.  Without await, execution can continue while
  // viewStates are being created.
  public static async initFrontstage(iModel: IModelConnection, vp: ScreenViewport) {

    this.imodel = iModel;
    this.vp = vp;
    this.viewState2d = await this.setup2dView("$PFD");
    this.viewStateCADTop = await this.setup3dView("$CAD-Model"); 
    this.viewStateCADBottom = await this.setup3dView("$Elements");

//    await this.setupSingleViewport();
  }

  public static async setup2dView(modelName: string) {

    const modelId = await cView.fetchModelIds(modelName);
    if (modelId.length == 0) throw new Error("No 2D models found in iModel.");

    const viewCreator = new ViewCreator2d(this.imodel);
    const options: ViewCreator2dOptions = {
      bgColor: ColorDef.white,
    };
    const viewState = await viewCreator.createViewForModel(modelId[0], options);
//    viewState.setExtents(viewState.getExtents());

    return viewState;
  }

  public static async setup3dView(modelName: string) {

    const modelId = await cView.fetchModelIds(modelName);
    const viewCreator3d = new ViewCreator3d(this.imodel);

    const options: ViewCreator3dOptions = { //!!OA not used
      cameraOn: true,                       // viewport initialized later
      skyboxOn: false,
      standardViewId: StandardViewId.Iso,
      useSeedView: false, //true, //!!OA - true adds ~3 sec delay - disabling 
      vpAspect: 1,
    };
    const viewState = await viewCreator3d.createDefaultView(undefined, modelId);

    return viewState;
  }

  public static getRealityModelViewport(): ScreenViewport {

    let rmVp = this.getViewportById("ViewportRealityModel");
    if(!rmVp) {
      rmVp = cView.getCurrentView();
    }
    
    return rmVp;
  }

  public static async setupWindowLayout(layout : string) {

    switch (layout) {
      case "1":
        await this.setupSingleViewport();
        break;
      case "2V":
        await this.setupTwoVerticalViewport();
        break;
      case "2H":
        await this.setupTwoHorizontalViewport();
        break;
      case "3":
        await this.setupThreeViewport();
        break;
      case "4":
        await this.setupFourViewport();
        break;
    }

    cMarker.setViewport(cView.getCurrentView());//!!OA
//    cMarker.setViewport(this.getViewportById("Viewport3"));
    // cMarker.disableMarkerSet("Issues");
    // cMarker.enableMarkerSet("Issues");
  }

  public static async setupSingleViewport() {
    const fsViews : cViewportConfig[] = [];

    const rmVp = this.getRealityModelViewport();
    fsViews.push({id: "ViewportRealityModel", viewState: rmVp.view });

    await this.setupFrontstage("single-group", StandardContentLayouts.singleView, fsViews);
    this.connectViewports();
  }

  public static async setupTwoVerticalViewport() {
    const fsViews : cViewportConfig[] = [];

    const rmVp = this.getRealityModelViewport();
    fsViews.push({id: "ViewportRealityModel", viewState: rmVp.view });
    fsViews.push({id: "Viewport2", viewState: this.viewStateCADTop });

    await this.setupFrontstage("two-V-group", StandardContentLayouts.twoVerticalSplit, fsViews);

    cView.initViewSetting(this.getViewportById("Viewport2"));
    this.connectViewports();
  }

  public static async setupTwoHorizontalViewport() {
    const fsViews : cViewportConfig[] = [];

    const rmVp = this.getRealityModelViewport();
    fsViews.push({id: "ViewportRealityModel", viewState: rmVp.view });
    fsViews.push({id: "Viewport2d", viewState: this.viewState2d });

    await this.setupFrontstage("two-H-group", StandardContentLayouts.twoHorizontalSplit, fsViews);
  }

  public static async setupThreeViewport() {
    const fsViews : cViewportConfig[] = [];

    const rmVp = this.getRealityModelViewport();
    fsViews.push({id: "ViewportRealityModel", viewState: rmVp.view });
    fsViews.push({id: "Viewport3", viewState: this.viewStateCADTop });
    fsViews.push({id: "Viewport2d", viewState: this.viewState2d });

    await this.setupFrontstage("three-group", StandardContentLayouts.threeViewsTwoOnBottom, fsViews);

    cView.initViewSetting(this.getViewportById("Viewport3"));
    this.connectViewports();
  }

  public static async setupFourViewport() {
    const fsViews : cViewportConfig[] = [];

    const rmVp = this.getRealityModelViewport();
    fsViews.push({id: "ViewportRealityModel", viewState: rmVp.view });
    fsViews.push({id: "Viewport2d", viewState: this.viewState2d });
    fsViews.push({id: "Viewport3", viewState: this.viewStateCADTop });
    fsViews.push({id: "Viewport4", viewState: this.viewStateCADBottom });
    await this.setupFrontstage("four-group", StandardContentLayouts.fourQuadrants, fsViews);

    cView.initViewSetting(this.getViewportById("Viewport3"));
    cView.initViewSetting(this.getViewportById("Viewport4"));
    this.connectViewports();
  }

  public static async setupFrontstage(fsId: string, layout: ContentLayoutProps, viewConfig: cViewportConfig[]) {
    const contentProps: ContentProps[] = [];

    let contentGroupProps: ContentGroupProps;

    const activeFrontstageDef = FrontstageManager.activeFrontstageDef;
    if (activeFrontstageDef) {
      for (const vc of viewConfig ) {
        contentProps.push({
          id: vc.id!,
          classId: IModelViewportControl,
          applicationData: {
            viewState: vc.viewState,
            iModelConnection: this.imodel,
          }
        });
      }

      contentGroupProps = {
        id: fsId,
        layout: layout,
        contents: contentProps,
      };

      if (activeFrontstageDef.contentGroupProvider) {
        contentGroupProps = 
        activeFrontstageDef.contentGroupProvider.applyUpdatesToSavedProps(contentGroupProps);
      }

      const contentGroup = new ContentGroup(contentGroupProps);
      await FrontstageManager.setActiveContentGroup(contentGroup);

    }
  }

  public static getViewportById(vpId : string): ScreenViewport {
    let vp : ScreenViewport;

    const activeFrontstage = FrontstageManager.activeFrontstageDef!;
    for (const aFs of activeFrontstage.contentControls) {
      if (aFs.name.includes(vpId)) {
        vp = aFs.viewport!;
      }
    }

    return vp!;
  }

  public static getActiveViewports(vsType : number = 0): Viewport[] {
    const vpList : Viewport[] = [];

    for (const fs of FrontstageManager.activeFrontstageDef!.contentControls) {
      const vp = fs.viewport;
      if (vp) {
        if (vsType == 2) {
          if (vp.view.is2d()) vpList.push(vp);
        } else if (vsType == 3) {
          if (vp.view.is3d()) vpList.push(vp);
        } else {
          vpList.push(vp);
        }
      }
    }

    return vpList;
  }

  public static connectViewports() {

    if (this.disconnect) {
      this.disconnect();
      this.disconnect = null;
    }

    const vp3d = this.getActiveViewports(3);
    if (vp3d.length == 1) return;

    this.disconnect = connectViewports(vp3d, this.setupViewportSync);
  }

  private static setupViewportSync = (sourceVp : Viewport) : SynchronizeViewports => {
  // Clone the frustum (once). 
    this.pose = sourceVp.view.savePose(); 
  // Get the clip 
    this.clip = sourceVp.view.getViewClip()!;
  
    return this.syncViewports;
  }

  private static syncViewports = (source: Viewport, target: Viewport) => { 
    target.setupFromView(this.pose);
    // const cClip = source.view.getViewClip()!;  //!!OA
    // const tClip = target.view.getViewClip();
    // const oClip = this.clip;
    if (target.view.getViewClip() != this.clip) {
      target.view.setViewClip(this.clip);
    }
  }
}

//   public static connectViewports() {

//     if (this.disconnect) {
//       this.disconnect();
//       this.disconnect = null;
//     }

//     const vp3d = this.getActiveViewports(3);
//     // if (vp3d.length > 1) {
//     //   // this.disconnect = connectViewportFrusta(vp3d);
//     //   // this.disconnect = connectViewportViews(vp3d);
//     //   this.twoWaySync.connect(vp3d[0], vp3d[1]);
//     // }
//     if (vp3d.length == 1) return;

//     this.disconnect = connectViewports(vp3d, (source) => {
//       // Clone the frustum (once). 
//       this.pose = source.view.savePose(); 
//       // Get the clip 
// //      this.clip = source.view.details.clipVector!; 
//       this.clip = source.view.getViewClip()!;

//       // Apply the frustum and clip to each target viewport 
//       return (_source, target) => { 
// //        const view = target.view.applyPose(pose); 
//         target.setupFromView(this.pose);
// //        if (view.details.clipVector != clip) view.details.clipVector = clip;
//         const cClip = _source.view.getViewClip()!;
// //        if (this.clip) {
//         if (target.view.getViewClip() != this.clip) {
// //          target.view.viewFlags = target.view.viewFlags.with("clipVolume", true);
//           target.view.setViewClip(this.clip);
//         } else {
// //          target.view.setViewClip();
//         }
//       };
//     });
//   }

//   private static setupViewportSync(sourceVp : Viewport) : SynchronizeViewports {
// //  private static setupViewportSync = (sourceVp : Viewport) : SynchronizeViewports => {

//     // Clone the frustum (once). 
//     cMultiViewport.pose = sourceVp.view.savePose(); 
//     // Get the clip 
// //      this.clip = source.view.details.clipVector!; 
//     cMultiViewport.clip = sourceVp.view.getViewClip()!;

//     return (_source, target) => { 
// //        const view = target.view.applyPose(pose); 
//         target.setupFromView(cMultiViewport.pose);
// //        if (view.details.clipVector != clip) view.details.clipVector = clip;
//         const cClip = _source.view.getViewClip()!;
// //        if (this.clip) {
//         if (target.view.getViewClip() != cMultiViewport.clip) {
// //          target.view.viewFlags = target.view.viewFlags.with("clipVolume", true);
//           target.view.setViewClip(cMultiViewport.clip);
//         } else {
// //          target.view.setViewClip();
//         }
//     };
//   };